import i18n from '../i18n';
import { Role } from '../types/auth';
import { OrderState, OrderType } from '../types/orders';
import { UNSPECIFIC } from '../types/utils';
import {
  canCreateContentInState,
  canRoleChangeEntityState,
} from './rolePermissions';
import { laneLabel } from './catalogLabels';
import { endRoadIdOf, segmentMeters, type RoadStretch } from './roadStretches';
import type { PkMeshVertex } from './pkFromMesh';
import type { AutoReview } from './segmentAutoReview';
import {
  RouteRoadRangeInput,
  RouteSegment,
  RouteState,
  SegmentTransferTarget,
  SurveillanceRoute,
} from '../types/surveillanceRoute';
import { isFreshReview } from './segmentReviewOverlay';
import { hasSyntheticId } from '../offline/pendingSegmentsFromQueue';
import { comparePk, pkMinMax } from './pkCompare';

// ═══════════════════════════════════════════════════════════════════
// STATE UTILITIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Obtiene el color de fondo para el estado de la ruta (V4 D5).
 */
export const getRouteStateBackgroundColor = (state: string): string => {
  switch (state) {
    case RouteState.OPEN:
      return '#FFF3E0';
    case RouteState.IN_PROGRESS:
      return '#E3F2FD';
    case RouteState.PAUSED:
      return '#FFF8E1';
    case RouteState.PENDING_VALIDATION:
      return '#FFFDE7';
    case RouteState.END:
      return '#E8F5E9';
    case RouteState.ANNULLED:
      return '#FFEBEE';
    default:
      return '#F5F5F5';
  }
};

// ═══════════════════════════════════════════════════════════════════
// STATE TRANSITION VALIDATORS (V4 D5)
// ═══════════════════════════════════════════════════════════════════
//
// Estados: open → in-progress → paused ↔ in-progress → pending-validation → end
//                                                                         ↘ annulled (desde cualquier no-terminal)

/**
 * Verifica si se puede iniciar un recorrido (solo desde `open`).
 */
export const canStartRoute = (route: SurveillanceRoute): boolean => {
  return route.state === RouteState.OPEN;
};

/**
 * Verifica si se puede pausar un recorrido (solo Tablet, desde `in-progress`).
 */
export const canPauseRoute = (route: SurveillanceRoute): boolean => {
  return route.state === RouteState.IN_PROGRESS;
};

/**
 * Verifica si se puede reanudar un recorrido (solo Tablet, desde `paused`).
 */
export const canResumeRoute = (route: SurveillanceRoute): boolean => {
  return route.state === RouteState.PAUSED;
};

/**
 * Verifica si se puede finalizar (pasar a `pending-validation`) desde `in-progress` o `paused`.
 */
export const canFinishRoute = (route: SurveillanceRoute): boolean => {
  return (
    route.state === RouteState.IN_PROGRESS || route.state === RouteState.PAUSED
  );
};

// Force-close del gestor (rv-recorrido-colgado 2026-07-22). Mirror de la
// constante backend STALE_ROUTE_HOURS (surveillance-route-state-validator):
// un RV se considera COLGADO si su actividad más reciente (max de start/end de
// tramos y registerInitDate) fue hace más de estas horas.
export const STALE_ROUTE_HOURS = 24;

/** Actividad más reciente del RV (ms) — max de tramos + registerInitDate. */
const mostRecentRouteActivityMs = (route: SurveillanceRoute): number => {
  const times: number[] = [];
  for (const seg of route.segments ?? []) {
    if (seg.startDate) times.push(new Date(seg.startDate).getTime());
    if (seg.endDate) times.push(new Date(seg.endDate).getTime());
  }
  if (route.registerInitDate)
    times.push(new Date(route.registerInitDate).getTime());
  return times.length ? Math.max(...times) : 0;
};

/** ¿El RV está colgado (sin actividad > STALE_ROUTE_HOURS)? */
export const isRouteStale = (route: SurveillanceRoute): boolean => {
  const last = mostRecentRouteActivityMs(route);
  if (!last) return true; // sin actividad ninguna → colgado por definición
  return Date.now() - last > STALE_ROUTE_HOURS * 60 * 60 * 1000;
};

/**
 * ¿Puede este rol forzar el cierre del recorrido? Coex/Capataz/SuperAdmin,
 * SOLO en estados operables (open/in-progress/paused) y SOLO si el RV está
 * colgado (mirror del guard `assertRouteIsStale` del backend). El botón se
 * oculta si no; el backend es la autoridad final (rechaza SR_ROUTE_NOT_STALE).
 */
export const canForceCloseRoute = (
  role: Role | undefined,
  route: SurveillanceRoute
): boolean => {
  const managerRoles: Array<Role | undefined> = [
    Role.Coex,
    Role.Capataz,
    Role.SuperAdmin,
  ];
  if (!managerRoles.includes(role)) return false;
  // SOLO in-progress/paused: un RV ya EMPEZADO (tiene tramos) que se quedó
  // colgado. Un `open` abandonado (nunca arrancó, sin trabajo que rescatar)
  // se ANULA, no se fuerza el cierre — el backend rechaza open (SR_FINISH_ONLY_ACTIVE).
  const startedAndOperable =
    route.state === RouteState.IN_PROGRESS || route.state === RouteState.PAUSED;
  return startedAndOperable && isRouteStale(route);
};

/**
 * Roles con permiso ampliado para editar el CONTENIDO de un Recorregut de
 * Vigilància (nombre, concesión, carretera, PKs, equipo, tripulación) más
 * allá del estado `open` — mismo patrón que `PRIVILEGED_EDIT_ROLES` del
 * botón "Editar" canónico de Order (`ShowOT/WorkOrder`), reaplicado aquí a
 * la entidad SurveillanceRoute (F5 2026-07-17, spec §2.4). Se declara APARTE
 * (no se importa desde ShowOT) porque son matrices de dominios distintos —
 * edición de campos de Order vs. edición de campos de SR — que no deben
 * derivar la una de la otra si cualquiera cambia en el futuro.
 */
const SR_PRIVILEGED_EDIT_ROLES: ReadonlySet<Role> = new Set([
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
  Role.RoomTeam,
]);

/**
 * P15 Fase 2 (2026-07-09) — roles que pueden vincular una OT externa
 * (genérica/campaña) a un recorrido o a un tramo DESDE LA OT: la card de la OT
 * y la etiqueta de aviso del trabajo (§ 16). La tablet no liga desde la OT; sí
 * desde su propio recorrido, si es la dueña del cronómetro
 * (`canLinkOrdersToRoute`), y el back la deja pasar con
 * `assertTabletOperatesRoute` (`linkOrderToSurveillanceRoute` y, desde el
 * 28/09, `linkExternalOrderToSegment`). Coincide hoy con
 * `PRIVILEGED_EDIT_ROLES`, pero se declara aparte a propósito: son permisos de
 * dominios distintos (editar campos de la orden vs. vincular al RV).
 */
export const ROLES_CAN_LINK_EXTERNAL_ORDER: readonly Role[] = [
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
  Role.RoomTeam,
];

export const canLinkExternalOrder = (
  role: Role | string | undefined | null
): boolean => ROLES_CAN_LINK_EXTERNAL_ORDER.includes(role as Role);

/**
 * ¿Puede el rol abrir el wizard EditRoute con el RV en su estado actual?
 * Regla (F5 2026-07-17, spec §2.4) — gating de la ENTIDAD SurveillanceRoute,
 * NUNCA del estado de la Order gemela:
 * - NUNCA en `end`/`annulled` (estados terminales, para todos los roles).
 * - `Tablet`: `open`/`in-progress`/`paused` (2026-08-05, espejo del backend).
 * - `SuperAdmin`/`Coex`/`Capataz`/`RoomTeam`: cualquier estado no terminal
 *   (`open`/`in-progress`/`paused`/`pending-validation`).
 * - Cualquier otro rol (p.ej. `OperatingTeam`): sin acceso.
 *
 * Recibe el estado en crudo (`RouteState`/string) — en el listado de
 * históricos usar el campo `specialTag` del row adaptado (`state` ya viene
 * traducido a variant de `SpecialTag` por `adaptSurveillanceRouteToOrder`).
 */
export const canEditSurveillanceRoute = (
  role: Role | string | undefined | null,
  state: RouteState | string | undefined | null,
  /**
   * 🔴 Quién pregunta y de quién es el recorrido (2026-09-23). Para el Tablet
   * son OBLIGATORIOS: hasta hoy bastaban rol y estado, y una tablet de la otra
   * patrulla —o de un equipo que ni es de vigilancia— podía abrir el recorrido
   * ajeno y cambiarle carreteras, tripulación o equipo, porque la tablet ve
   * todos los recorridos del tenant. Mismo agujero que se cerró para las
   * básicas el 2026-09-04. Sin ellos, el Tablet no ve el lápiz.
   *
   * Solo interfaz: el servidor (`saveSurveillanceRoute`) sigue mirando rol y
   * estado. Endurecerlo allí descartaría ediciones encoladas sin cobertura si
   * al sincronizar el recorrido tuviera otro dueño — decisión pendiente, ver
   * el spec `2026-09-23-rv-apoyo-entre-tramos-design.md` § 6.
   */
  user?: Parameters<typeof canOperateTimer>[0],
  route?: Parameters<typeof canOperateTimer>[1]
): boolean => {
  if (!role || !state) return false;
  if (state === RouteState.END || state === RouteState.ANNULLED) return false;
  if (role === Role.Tablet) {
    // La regla del cronómetro: ser de un equipo del recorrido y, si la
    // patrulla está viva, ser su dueño. El estado que manda es el de quien
    // llama (puede venir de una fila adaptada), así que se le pasa explícito.
    if (!route || !canOperateTimer(user, { ...route, state })) return false;
    // 2026-08-05 (B1): `paused` entra. En el RV significa *sin tramo abierto*
    // — la patrulla circulando entre tramos, no "parado". Espejo exacto de
    // SAVE_EDITABLE_STATES_BY_ROLE del backend
    // (surveillance-route-state-validator.service.ts). `pending-validation`
    // sigue fuera: ya entregado, primero des-entrega (reopen → paused).
    return (
      state === RouteState.OPEN ||
      state === RouteState.IN_PROGRESS ||
      state === RouteState.PAUSED
    );
  }
  return SR_PRIVILEGED_EDIT_ROLES.has(role as Role);
};

/**
 * La edición que añade una vía de APOYO al recorrido (2026-09-23): la del
 * botón «Añadir» del aviso del detector, cuando la patrulla está en una vía de
 * su concesión que el recorrido no declara.
 *
 * Reenvía los rangos declarados porque el servidor los guarda pegados a la
 * lista de carreteras y, si esta cambia sin ellos, los vacía. Sin
 * `__typename`: vienen de la caché y el esquema de entrada los rechazaría. La
 * vía nueva no lleva rango —nadie lo ha dicho— y se valida contra sus extremos.
 */
export const buildAddRoadToRouteInput = (
  route: {
    id: string;
    roadIDs?: string[] | null;
    roadRanges?: Array<{
      roadId: string;
      pkInitKm: number;
      pkInitMeter: number;
      pkEndKm: number;
      pkEndMeter: number;
    }> | null;
  },
  roadId: string,
  clientOpId: string
): {
  id: string;
  roadIds: string[];
  roadRanges: RouteRoadRangeInput[];
  clientOpId: string;
} => {
  const roadIds = [...(route.roadIDs ?? [])];
  if (!roadIds.includes(roadId)) roadIds.push(roadId);
  return {
    id: route.id,
    roadIds,
    roadRanges: (route.roadRanges ?? []).map(
      ({ roadId: id, pkInitKm, pkInitMeter, pkEndKm, pkEndMeter }) => ({
        roadId: id,
        pkInitKm,
        pkInitMeter,
        pkEndKm,
        pkEndMeter,
      })
    ),
    clientOpId,
  };
};

/**
 * ¿Puede este rol CORREGIR los tramos de un recorrido en este estado?
 *
 * Corregir un tramo (PK, sentido, carril, horas) es una herramienta de
 * GESTIÓN, no un gesto de campo: el operario no corrige, corrige quien
 * revisa. Con trabajo en curso no se permite (hay cronómetro vivo) y en
 * estados terminales hay que reabrir antes; de ahí que solo valgan `paused`
 * y `pending-validation`. Espejo del allowlist del resolver de backend.
 *
 * Vive aquí, y no en cada página, porque desde 2026-08-05 hay DOS puntos de
 * entrada al mismo modal (la tabla de Tramos de "Vista previa" y la card del
 * tramo en "Órdenes asociadas"). Con la condición copiada en los dos sitios,
 * cambiar el permiso en uno y olvidarlo en el otro deja un botón que abre un
 * modal cuyo guardado el backend rechaza — el fallo se vería solo al enviar.
 */
export const canEditRouteSegments = (
  role: Role | string | undefined | null,
  state: RouteState | string | undefined | null
): boolean => {
  if (!role || !state) return false;
  if (!SR_PRIVILEGED_EDIT_ROLES.has(role as Role)) return false;
  return state === RouteState.PAUSED || state === RouteState.PENDING_VALIDATION;
};

/**
 * ¿Puede este rol cambiar la CAUSA de los tramos? (D6)
 *
 * Es una herramienta de reparación de GESTIÓN: espejo del allowlist del
 * resolver `updateSegmentPauseCause` (SuperAdmin/Coex/Capataz/sala). El Tablet
 * no — antes de 2026-07-13 la etiqueta se le mostraba clicable y el guardado
 * acababa en «Forbidden resource».
 *
 * A diferencia de `canEditRouteSegments`, NO depende del estado del recorrido:
 * el backend solo exige que el tramo esté cerrado.
 *
 * Vive aquí porque desde 2026-10-07 hay DOS puntos de entrada al mismo modal
 * (la etiqueta «Causa: X» de la card del tramo y la columna «Cambiar causa» de
 * la tabla de Tramos): con la condición copiada en los dos, cambiar el permiso
 * en uno y olvidarlo en el otro deja un botón que el backend rechaza.
 */
export const canRoleChangeSegmentCause = (
  role: Role | string | undefined | null
): boolean => {
  if (!role || !SR_PRIVILEGED_EDIT_ROLES.has(role as Role)) return false;
  return canRoleChangeEntityState(role as Role, OrderType.SurveillanceRoute);
};

/**
 * ¿Tiene este tramo una causa que se pueda cambiar? Solo los tramos CERRADOS
 * (el backend rechaza los abiertos) y que ya tengan causa: la de un tramo
 * abierto se elige al cerrarlo, en el cronómetro. Y no ANULADOS (revisión M1,
 * 15-oct): ya no cuentan, y con «Actuación» colgaría una básica de un tramo
 * muerto. Como «Transferir».
 */
export const isSegmentCauseChangeable = (segment: {
  id?: string | null;
  endDate?: string | number | null;
  pauseCause?: string | null;
  state?: string | null;
}): boolean =>
  Boolean(segment.id && segment.endDate && segment.pauseCause) &&
  segment.state !== 'annulled';

/**
 * ¿Se le pueden poner o cambiar vínculos a este tramo (aviso a / de sala o
 * actuación básica)? Uno ANULADO no: ya no cuenta, y el servidor aceptaba
 * ligarle una OT o colgarle una básica (revisión M1 y QA T3.1+a, 15-oct). La
 * leen la celda «Vincular» de la tabla y la card del tramo; mirar lo que ya
 * tiene vinculado («Ver») sí se puede.
 */
export const canSegmentTakeLinks = (segment: {
  state?: string | null;
}): boolean => segment.state !== 'annulled';

/** Causas de parada de un tramo, en el orden del desplegable (V4 D7). */
export const SEGMENT_PAUSE_CAUSES = ['basic', 'action', 'external-call'] as const;

/**
 * ¿Se puede cambiar la causa de los tramos de ESTE recorrido? (revisión I1,
 * 15-oct) — el rol de gestión (`canRoleChangeSegmentCause`) y el recorrido sin
 * cerrar. Con el parte validado o anulado el back acepta el cambio, pero ahí
 * ni el aviso ni la actuación se pueden resolver después y el tramo se
 * quedaba colgado. Mismo corte que «Corregir», «Parada» o «Transferir». La
 * usan la columna «Cambiar causa» y la etiqueta «Causa: X» de la card.
 */
export const canChangeSegmentCauseInRoute = (
  role: Role | string | undefined | null,
  routeState: string | undefined | null
): boolean =>
  canRoleChangeSegmentCause(role) &&
  Boolean(routeState) &&
  routeState !== RouteState.END &&
  routeState !== RouteState.ANNULLED;

/**
 * Causas que ofrece el desplegable de la causa de un tramo (15-oct).
 *
 * Pasar la causa a «Actuación» lanza la cadena que CREA la básica del tramo,
 * así que solo se ofrece si en ESE recorrido se puede crear: lo decide quien
 * abre el desplegable con la regla que ya existe (`canLinkBasicActionFromRoute`
 * en la tabla, el `canCreateBasicAction` de `ShowOT` en la card). Sala nunca
 * puede; antes se le ofrecía y el back aceptaba el cambio de causa —soltando
 * la OT del aviso o la básica—, le negaba la básica y el tramo se quedaba
 * pendiente de actuación.
 *
 * La causa ACTUAL va siempre: sin ella el desplegable abriría vacío, y
 * dejarla no lanza nada (la cadena solo arranca si la causa CAMBIA a
 * «Actuación»).
 */
export const selectableSegmentCauses = (
  canChooseAction: boolean,
  currentCause?: string | null
): string[] =>
  SEGMENT_PAUSE_CAUSES.filter(
    (cause) => cause !== 'action' || cause === currentCause || canChooseAction
  );

/** Forma mínima del usuario que necesitan los permisos del editor de tramo. */
type SegmentEditorUser =
  | { id?: string; rolActive?: string | null; teamIDs?: string[] }
  | undefined
  | null;

/** Forma mínima del recorrido que necesitan los permisos del editor de tramo. */
type SegmentEditorRoute =
  | {
      teamIDs?: string[] | null;
      timerOwnerId?: string | null;
      state?: string | null;
    }
  | undefined
  | null;

/**
 * ¿Puede este operario rectificar SU propio tramo? (spec 2026-08-15)
 *
 * El Tablet registra los tramos en campo y hasta ahora no podía enmendar ni una
 * errata: si se equivocaba de sentido, de carril o al escribir el motivo del
 * cierre, tenía que pedírselo a gestión. Ahora rectifica **lo que él mismo
 * teclea o elige**; los kilómetros y las horas siguen siendo intocables porque
 * son la medida del trabajo y lo que se factura.
 *
 * Ventana: mientras la patrulla está VIVA. Incluye `in-progress` a propósito —
 * cerrado un tramo, el operario arranca el siguiente en segundos, y ceñirse a
 * `paused` dejaría fuera justo el momento en que se da cuenta. Al ENTREGAR
 * (`pending-validation`) se acaba: su palabra ya está dada.
 *
 * 🔴 El freno de los estados es EXPLÍCITO y no sobra: `canOperateTimer` no mira
 * terminales (no le hace falta donde se usa hoy), así que sin esta línea la
 * patrulla dueña editaría un recorrido cerrado o anulado. Misma lección que
 * dejó escrita `canAttachSegmentPhotos`.
 *
 * 🔴 Es una puerta de la INTERFAZ. El servidor valida EQUIPO
 * (`validateTimerOwnership`), no `timerOwnerId`: el candado fino de «solo el
 * dueño» vive aquí, igual que en el resto del ciclo del cronómetro.
 */
export const canCorrectOwnSegment = (
  user: SegmentEditorUser,
  route: SegmentEditorRoute
): boolean => {
  if (!user || !route) return false;
  if (
    route.state !== RouteState.IN_PROGRESS &&
    route.state !== RouteState.PAUSED
  ) {
    return false;
  }
  return canOperateTimer(user, route);
};

/**
 * ¿Se pinta el lápiz de corregir el tramo?
 *
 * Unión de las dos audiencias del MISMO modal: gestión
 * (`canEditRouteSegments`) y el operario dueño (`canCorrectOwnSegment`). Vive
 * en un solo sitio por la misma razón que ya documenta `canEditRouteSegments`:
 * hay DOS puntos de entrada (la tabla de Tramos de «Vista previa» y la card de
 * «Órdenes asociadas»), y con la condición copiada en los dos, cambiar una y
 * olvidar la otra deja un botón cuyo guardado el backend rechaza — un fallo que
 * solo se ve al enviar.
 */
export const canOpenSegmentEditor = (
  user: SegmentEditorUser,
  route: SegmentEditorRoute
): boolean =>
  canEditRouteSegments(user?.rolActive, route?.state) ||
  canCorrectOwnSegment(user, route);

/**
 * ¿Este tramo tiene trabajo SIN SINCRONIZAR?
 *
 * Son DOS casos, y hacen falta los dos — `pendingSegmentsFromQueue` marca ambos
 * con `isPending`:
 *  - un tramo creado sin cobertura, que ni existe en el servidor y lleva un id
 *    sintético `pending-…`;
 *  - un tramo que SÍ existe en el servidor pero cuyo CIERRE sigue en la cola:
 *    id real y bandera puesta.
 *
 * 🔴 Mirar solo el id deja fuera el segundo, que es el MÁS habitual —basta con
 * cerrar un tramo sin cobertura— y fue el fallo que cazó el usuario probando
 * (2026-08-15): la fila salía atenuada con ⏳ y aun así ofrecía «Corregir».
 *
 * Un solo criterio para sus tres consumidores: el ⏳, el atenuado de la fila y
 * el candado del enlace de corregir. Corregir algo que aún no ha llegado al
 * servidor es pedirle que arregle lo que todavía no tiene.
 */
export const isSegmentPendingSync = (
  segment: (RouteSegment & { isPending?: boolean }) | null | undefined
): boolean =>
  !!segment && (segment.isPending === true || hasSyntheticId(segment));

// ═══════════════════════════════════════════════════════════════════
// TRAMOS REVISADOS (2026-10-27)
// ═══════════════════════════════════════════════════════════════════

/** Roles que marcan tramos como revisados. Espejo del backend (sala NO marca). */
const SEGMENT_REVIEW_ROLES: ReadonlySet<Role> = new Set([
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
]);

/**
 * ¿Puede este rol marcar tramos como revisados en este recorrido? (spec D8)
 * Un recorrido anulado no se revisa: ya no hay nada que dar por bueno.
 */
export const canReviewRouteSegments = (
  role: Role | string | undefined | null,
  route: Pick<SurveillanceRoute, 'state'> | null | undefined
): boolean => {
  if (!role || !route) return false;
  if (!SEGMENT_REVIEW_ROLES.has(role as Role)) return false;
  return route.state !== RouteState.ANNULLED;
};

/**
 * Un tramo se puede marcar si está cerrado (tiene `endDate`) y ya existe en el
 * servidor: marcar un tramo vivo o uno que aún está en la cola offline no
 * tiene destino al que apuntar.
 */
export const isSegmentReviewable = (segment: RouteSegment): boolean =>
  !!segment.endDate && !isSegmentPendingSync(segment);

/**
 * ¿Cuenta el tramo como revisado y al día? El `overlay` (marcas aún sin
 * confirmar por el servidor) manda sobre `myReview`; una marca de overlay no
 * está nunca obsoleta.
 */
const isReviewedAndFresh = (
  segment: RouteSegment,
  overlay?: ReadonlyMap<string, boolean>,
  autoReviews?: ReadonlyMap<string, AutoReview>
): boolean => {
  // El automático manda sobre la marca local y la del servidor.
  if (autoReviews?.has(segment.id)) return true;
  const override = overlay?.get(segment.id);
  if (override !== undefined) return override;
  return isFreshReview(segment);
};

/** Progreso «revisados / revisables» de una lista de tramos. */
export const computeReviewProgress = (
  segments: RouteSegment[],
  overlay?: ReadonlyMap<string, boolean>,
  autoReviews?: ReadonlyMap<string, AutoReview>
): { done: number; total: number } => {
  let done = 0;
  let total = 0;
  for (const segment of segments) {
    if (!isSegmentReviewable(segment)) continue;
    total += 1;
    if (isReviewedAndFresh(segment, overlay, autoReviews)) done += 1;
  }
  return { done, total };
};

/**
 * Índice (en el orden recibido) del primer tramo revisable sin marca viva o con
 * marca obsoleta (ámbar). `-1` si no queda ninguno.
 */
export const findNextUnreviewedIndex = (
  segments: RouteSegment[],
  overlay?: ReadonlyMap<string, boolean>,
  autoReviews?: ReadonlyMap<string, AutoReview>
): number =>
  segments.findIndex(
    (segment) =>
      isSegmentReviewable(segment) &&
      !isReviewedAndFresh(segment, overlay, autoReviews)
  );

/**
 * ¿Tiene el usuario su marca en este tramo, viva o en ámbar? El `overlay` manda
 * (un `false` local es «la acabo de quitar»). Decide si tras corregir el tramo
 * o cambiarle la causa se reenvía la marca (spec § 9: que tu propia corrección
 * no te lo deje en ámbar).
 */
export const hasOwnReviewMark = (
  segment: RouteSegment,
  overlay?: ReadonlyMap<string, boolean>
): boolean => {
  const override = overlay?.get(segment.id);
  if (override !== undefined) return override;
  return !!segment.myReview;
};

/**
 * Orden de la tabla de Tramos: cronológico por `startDate`. `sort` es estable,
 * así que a igual hora se respeta el orden recibido. Un solo criterio para la
 * tabla y para «Siguiente sin revisar», que tiene que recorrer el mismo orden.
 */
export const sortSegmentsByStartDate = <T extends Pick<RouteSegment, 'startDate'>>(
  segments: T[]
): T[] =>
  [...segments].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  );

/** Lo que dice el aviso al validar (D7): cuántos faltan y cuántos de ellos en ámbar. */
export type ValidateReviewWarning = { unreviewed: number; changed: number };

/**
 * Aviso al validar con tramos sin revisar (D7, aprobado 27-oct). NO bloquea.
 * Solo tiene sentido si el usuario ha empezado a revisar este recorrido (marcó
 * al menos uno, vivo o en ámbar) y aún le faltan: a quien no usa las marcas no
 * se le pregunta nada. `null` = no avisar. El rol lo decide quien llama.
 */
export const computeValidateReviewWarning = (
  segments: RouteSegment[],
  overlay?: ReadonlyMap<string, boolean>,
  autoReviews?: ReadonlyMap<string, AutoReview>
): ValidateReviewWarning | null => {
  let marked = 0;
  let unreviewed = 0;
  let changed = 0;
  for (const segment of segments) {
    if (!isSegmentReviewable(segment)) continue;
    if (hasOwnReviewMark(segment, overlay)) marked += 1;
    // Un automático no es pendiente; `marked` sigue siendo solo marcas propias.
    if (isReviewedAndFresh(segment, overlay, autoReviews)) continue;
    unreviewed += 1;
    // Ámbar: marca del servidor obsoleta y sin gesto local encima.
    if (!overlay?.has(segment.id) && segment.myReview?.stale) changed += 1;
  }
  return marked > 0 && unreviewed > 0 ? { unreviewed, changed } : null;
};

/** Qué versión del editor abre este usuario. */
export type SegmentEditorMode = 'management' | 'operator';

/**
 * Las dos ventanas se SOLAPAN en `paused`, así que hace falta un desempate y lo
 * gana el rol: un Coex en `paused` corrige el parte entero, un Tablet en
 * `paused` solo lo suyo. Se resuelve aquí, una vez, para que las dos entradas
 * no puedan discrepar sobre qué modal abren.
 */
export const segmentEditorMode = (
  user: SegmentEditorUser,
  route: SegmentEditorRoute
): SegmentEditorMode | null => {
  if (canEditRouteSegments(user?.rolActive, route?.state)) return 'management';
  if (canCorrectOwnSegment(user, route)) return 'operator';
  return null;
};

/**
 * ¿Puede este usuario ADJUNTAR fotos a los tramos de este recorrido?
 *
 * No inventa una regla nueva: es la UNIÓN de las dos que ya gobiernan el RV.
 *
 *  - **La patrulla dueña** (`canOperateTimer`): Tablet que comparte equipo con
 *    el recorrido y, si la patrulla está viva y tiene dueño, solo su dueño. Es
 *    quien hizo la intervención y quien saca la foto.
 *  - **Gestión** (`canEditRouteSegments`): los mismos roles y los mismos
 *    estados que para CORREGIR un tramo (`paused` / `pending-validation`) —
 *    quien revisa el parte puede completarlo.
 *
 * Antes esto era «cualquier rol mientras el recorrido no esté cerrado», que
 * dejaba a una tablet de OTRA patrulla meter fotos en un tramo ajeno: el
 * listado le enseña los recorridos de las demás marcados «· patrulla».
 *
 * 🔴 Es una puerta de la INTERFAZ. `createImage` no tiene `@Roles` en el
 * backend, así que la regla del servidor va aparte.
 */
export const canAttachSegmentPhotos = (
  user:
    | { id?: string; rolActive?: string | null; teamIDs?: string[] }
    | undefined
    | null,
  route:
    | {
        teamIDs?: string[] | null;
        timerOwnerId?: string | null;
        state?: string | null;
      }
    | undefined
    | null
): boolean => {
  if (!user || !route) return false;

  // 🔴 Freno explícito. `canOperateTimer` NO mira estados terminales —no le
  // hace falta, porque donde se usa ya están filtrados—, así que sin esta
  // línea la patrulla dueña podría añadir fotos a un recorrido ya cerrado o
  // anulado. Cazado por su test.
  if (route.state === RouteState.END || route.state === RouteState.ANNULLED) {
    return false;
  }

  if (canOperateTimer(user, route)) return true;
  return canEditRouteSegments(user.rolActive, route.state);
};

/**
 * ¿Puede este usuario CREAR una actuación básica en este recorrido, o
 * VINCULAR una actuación a uno de sus tramos?
 *
 * Para la tablet es EXACTAMENTE la regla del cronómetro (`canOperateTimer`):
 * compartir equipo con el recorrido y, si la patrulla está viva, ser su
 * dueña. Antes bastaba con el rol y el estado del recorrido
 * (`canCreateContentInState`), y como la tablet ve todos los recorridos del
 * tenant, dos toques la metían en el de otra patrulla y la básica nacía
 * colgando del recorrido equivocado. Le pasó al usuario el 2026-09-04.
 *
 * Gestión (super-admin, coex, capataz) crea en cualquiera, como hasta ahora.
 * Sala no crea básicas.
 *
 * 🔴 Esta puerta de la interfaz importa el doble sin cobertura: la básica se
 * encola y el backend solo puede rechazarla al sincronizar, y un 403 de
 * permisos descarta la operación de la cola. Si el botón no sale, no se
 * encola nada que vaya a perderse. El servidor aplica la misma regla en
 * `saveAction` y `linkOrderToSegment` para cerrar la petición a mano.
 */
export const canCreateBasicActionInRoute = (
  user:
    | { id?: string; rolActive?: string | null; teamIDs?: string[] }
    | undefined
    | null,
  route:
    | {
        teamIDs?: string[] | null;
        timerOwnerId?: string | null;
        state?: string | null;
      }
    | undefined
    | null
): boolean => {
  if (!user || !route) return false;
  if (route.state === RouteState.END || route.state === RouteState.ANNULLED) {
    return false;
  }
  if (user.rolActive === Role.Tablet) return canOperateTimer(user, route);
  return (
    user.rolActive === Role.SuperAdmin ||
    user.rolActive === Role.Coex ||
    user.rolActive === Role.Capataz
  );
};

/**
 * Roles que VEN el botón «Crear Actuación» de «Órdenes asociadas» dentro de un
 * recorrido (decisión del usuario, 2026-10-09).
 *
 * Solo gestión. La patrulla crea la básica por el cronómetro (cerrar el tramo
 * con causa «Actuación») y, si ese cierre se quedó sin básica —sin cobertura,
 * por ejemplo—, por la pastilla «Vincular a actuación» de ese tramo. El botón
 * crea una básica suelta, sin tramo, y queda como herramienta de gestión para
 * corregir cuando la patrulla se equivoca en un tramo.
 *
 * Se declara aparte de `canCreateBasicActionInRoute` a propósito: aquella
 * gobierna TAMBIÉN la pastilla del tramo y el vincular/desvincular, que la
 * tablet conserva. Esto decide solo si el botón se pinta; si sale habilitado
 * lo siguen decidiendo rol × estado y `canCreateBasicActionInRoute`.
 *
 * 🔴 Es una puerta de la INTERFAZ y no se replica en el servidor: el
 * cronómetro, la pastilla y el botón crean con la misma `saveAction` y el
 * backend no los distingue. Rechazarla allí descartaría las básicas que la
 * tablet encola sin cobertura (`.claude/rules/guard-nuevo-cola-offline.md`).
 */
export const ROLES_SEE_CREATE_ACTION_BUTTON_IN_ROUTE: readonly Role[] = [
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
];

export const canSeeCreateActionButtonInRoute = (
  role: Role | string | undefined | null
): boolean => ROLES_SEE_CREATE_ACTION_BUTTON_IN_ROUTE.includes(role as Role);

/**
 * ¿Puede este usuario vincular/desvincular OTs al recorrido (cross-link 3,
 * 2026-09-22)? Gestión y sala siempre (salvo recorrido cerrado o anulado);
 * la tablet solo si opera el cronómetro — la misma puerta que crear una
 * básica. La página decide y la sección obedece.
 */
export const canLinkOrdersToRoute = (
  user:
    | { id?: string; rolActive?: string | null; teamIDs?: string[] }
    | undefined
    | null,
  route:
    | {
        teamIDs?: string[] | null;
        timerOwnerId?: string | null;
        state?: string | null;
      }
    | undefined
    | null
): boolean => {
  if (!user || !route) return false;
  if (route.state === RouteState.END || route.state === RouteState.ANNULLED) {
    return false;
  }
  if (user.rolActive === Role.RoomTeam) return true;
  return canCreateBasicActionInRoute(user, route);
};

/**
 * ¿Puede este usuario lanzar «Vincular a actuación» desde un tramo con causa
 * Actuación que se quedó sin básica? Es la condición de la card del tramo
 * (`ShowOT`: `canCreateContentInState(...'action') && canCreateBasicAction`),
 * aquí con el estado del recorrido en lugar del de su Order gemela — van uno a
 * uno (`mapRouteStateToTagVariant`). La usa la columna «Vincular» de la tabla
 * de Tramos (2026-10-08): con la condición copiada en dos sitios, cambiarla en
 * uno y olvidarla en el otro dejaría un botón cuya básica el back rechaza al
 * sincronizar — y un 403 en la cola descarta la operación.
 */
export const canLinkBasicActionFromRoute = (
  user:
    | { id?: string; rolActive?: string | null; teamIDs?: string[] }
    | undefined
    | null,
  route:
    | {
        teamIDs?: string[] | null;
        timerOwnerId?: string | null;
        state?: string | null;
      }
    | undefined
    | null
): boolean => {
  if (!user?.rolActive || !route?.state) return false;
  return (
    canCreateContentInState(
      user.rolActive as Role,
      route.state as OrderState,
      'action'
    ) && canCreateBasicActionInRoute(user, route)
  );
};

/**
 * Fila Order-like para `InputRadioTagTable` a partir de un recorrido (modal
 * «Vincular recorrido», cross-link 3). Mismo espíritu que `adaptSegmentToOrder`:
 * la tabla es de Orders y el recorrido se disfraza con lo que tiene.
 */
export const adaptRouteToOrder = (route: SurveillanceRoute) => {
  const registerInitDate = route.registerInitDate || route.creationDate || '';
  return {
    id: route.id,
    roid: buildRoidFallback(route) || route.id,
    name: route.name || '',
    state: mapRouteStateToTagVariant(route.state),
    specialTag: '',
    creationDate: route.creationDate || '',
    date: registerInitDate,
    registerInitDate,
    derivedEndDate: '',
    description: route.name || '',
    pkInit: formatPK(route.pkInitKm, route.pkInitMeter),
    pkEnd: formatPK(route.pkEndKm, route.pkEndMeter),
    concession: '',
    road: '',
    capitol: '',
    subCapitol: '',
    user: '',
    userName: '',
  };
};

/**
 * Fila Order-like para `InputRadioTagTable` a partir de un recorrido CANDIDATO
 * de «Transferir tramo». 14-oct (feedback del usuario): los destinos salen en
 * la tabla de selección de siempre, como el «Transferir» de actuaciones, en vez
 * de tarjetas propias. Las fechas son el primer inicio y el último fin de sus
 * tramos: es lo que distingue dos recorridos que se llaman igual. El encaje
 * (`fits`/`reasonCode`) no va en la fila: lo enseña el modal al marcarla.
 */
export const adaptTransferTargetToOrder = (target: SegmentTransferTarget) => {
  // Un recorrido SIN tramos llega sin fechas (null): se pasan vacías y la
  // tabla pinta «—» (QA 15-oct, T2.10+vacío; antes reventaba la celda).
  const registerInitDate = target.firstStart ?? '';
  return {
    id: target.routeId,
    roid: target.roid,
    name: target.name ?? '',
    state: '',
    specialTag: '',
    creationDate: registerInitDate,
    date: registerInitDate,
    registerInitDate,
    derivedEndDate: target.lastEnd ?? '',
    description: '',
    track: '',
    direction: '',
    pkInit: '',
    pkEnd: '',
    concession: '',
    road: '',
    capitol: '',
    subCapitol: '',
    predefinedOrder: '',
    team: '',
  };
};

/**
 * Verifica si un recorrido está activo (se pueden crear tramos — estados operables).
 */
export const isRouteActive = (route: SurveillanceRoute): boolean => {
  return (
    route.state === RouteState.OPEN ||
    route.state === RouteState.IN_PROGRESS ||
    route.state === RouteState.PAUSED
  );
};

/**
 * Verifica si un recorrido está en un estado terminal (end o annulled).
 * Reemplaza al antiguo `isRouteCompleted`.
 */
export const isRouteFinal = (route: SurveillanceRoute): boolean => {
  return route.state === RouteState.END || route.state === RouteState.ANNULLED;
};

/**
 * @deprecated V4 D5: usar `isRouteFinal` que cubre los 2 estados terminales (end + annulled).
 * Mantiene nombre para compatibilidad con código llamante no migrado.
 */
export const isRouteCompleted = (route: SurveillanceRoute): boolean => {
  return isRouteFinal(route);
};

// ═══════════════════════════════════════════════════════════════════
// PK FORMATTERS
// ═══════════════════════════════════════════════════════════════════

/**
 * Formatea un PK (Punto Kilométrico) para mostrar
 * @param km Kilómetros
 * @param meter Metros (0-9999; en re-estacionamientos el offset supera 1000 — PK con exceso)
 * @returns String formateado como "KM+MMM" (padStart a 3, sin truncar 4 dígitos)
 */
export const formatPK = (km: number, meter: number): string => {
  const meterPadded = meter.toString().padStart(3, '0');
  return `${km}+${meterPadded}`;
};

/** Con qué abrir el asistente de un tramo que REANUDA el recorrido. */
export type ResumeSeed = {
  pkInit: { km: number; meter: number };
  /** La vía por la que el tramo anterior ACABÓ, si se conoce. */
  roadId?: string | null;
};

/**
 * El PK con el que sembrar «Iniciar tramo» al reanudar tras una pausa.
 *
 * ── Por qué hace falta ────────────────────────────────────────────────────
 *
 * Cerrar un tramo deja el recorrido en `paused`, y con `paused` la captura de
 * GPS se apaga entera: el `watchPosition` de la web y el servicio nativo, que
 * el guardián ata a los recorridos `in-progress`. Así que al pulsar «Iniciar»
 * el receptor lleva toda la pausa apagado y su primer fix es el lento — con el
 * agravante de que el modal se rinde a los pocos segundos y entonces ya no
 * llega nunca. El campo se quedaba vacío y el operario tecleaba el PK a mano,
 * al borde de la calzada.
 *
 * ── Por qué ESTE dato y no la posición ────────────────────────────────────
 *
 * El tramo nuevo empieza donde acabó el anterior, y ese PK ya está guardado y
 * además CONFIRMADO por el operario al cerrarlo. No hay que proyectar nada
 * contra la malla ni preguntar al backend: es un PK, no unas coordenadas. El
 * rastro local también sabría dónde se cerró, pero son coordenadas — y pasarlas
 * a PK cuesta una consulta con red.
 *
 * Es una SEMILLA, no una respuesta: el GPS la pisa en cuanto contesta.
 */
export const resumeSeedFromSegments = (
  segments: RouteSegment[] | undefined | null
): ResumeSeed | null => {
  if (!segments || segments.length === 0) return null;

  const lastClosed = segments
    // 🔴 `!= null` y no veracidad: `0+000` es un PK legítimo y `pkEndMeter: 0`
    // es *falsy*. Filtrar por verdad tiraría el kilómetro 0 y el metro 0, que
    // es el mismo fallo que tuvo `indexExpedition === 0`.
    .filter(
      (s) => !!s.endDate && s.pkEndKm != null && s.pkEndMeter != null
    )
    .sort(
      (a, b) =>
        new Date(b.endDate as string).getTime() -
        new Date(a.endDate as string).getTime()
    )[0];

  if (!lastClosed) return null;

  return {
    pkInit: {
      km: lastClosed.pkEndKm as number,
      meter: lastClosed.pkEndMeter as number,
    },
    // 🔴 La del FINAL, no la de salida. Un tramo que cruzó de la troncal al
    // ramal acaba en el ramal, y el PK que acompaña a esta semilla es el del
    // ramal: heredar la vía de salida dejaría el par descasado y devolvería al
    // operario a una carretera por la que ya no va.
    roadId: endRoadIdOf(lastClosed),
  };
};

export type PkInterval = {
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
};

/**
 * Verifica si dos intervalos de PK se solapan. Dos intervalos `[a,b]` y `[c,d]`
 * se solapan si `a < d` y `c < b`. Puntos compartidos (a === d o b === c) no
 * cuentan como overlap porque tramos consecutivos pueden compartir PKs.
 */
export const doPkIntervalsOverlap = (a: PkInterval, b: PkInterval): boolean => {
  // 🔴 Extremos ordenados con `pkMinMax`: con `Math.min/max` sobre el total en
  // metros, un intervalo que cruza un kilómetro largo se construye del revés.
  const [aMin, aMax] = pkMinMax(
    { km: a.pkInitKm, meter: a.pkInitMeter },
    { km: a.pkEndKm, meter: a.pkEndMeter }
  );
  const [bMin, bMax] = pkMinMax(
    { km: b.pkInitKm, meter: b.pkInitMeter },
    { km: b.pkEndKm, meter: b.pkEndMeter }
  );
  return comparePk(aMin, bMax) < 0 && comparePk(bMin, aMax) < 0;
};

/**
 * Verifica si un candidato de tramo se solapa con alguno de los tramos
 * existentes del recorrido. Si se pasa `excludeSegmentId`, ese tramo se ignora
 * (util al editar un tramo existente).
 */
export const findOverlappingSegment = <
  T extends PkInterval & { id?: string; direction?: string },
>(
  candidate: PkInterval & { direction?: string },
  existing: T[],
  options: { excludeSegmentId?: string; sameDirectionOnly?: boolean } = {}
): T | null => {
  const { excludeSegmentId, sameDirectionOnly = false } = options;
  for (const segment of existing) {
    if (excludeSegmentId && segment.id === excludeSegmentId) continue;
    if (
      sameDirectionOnly &&
      candidate.direction &&
      segment.direction &&
      candidate.direction !== segment.direction
    ) {
      continue;
    }
    if (doPkIntervalsOverlap(candidate, segment)) return segment;
  }
  return null;
};

/**
 * Formatea el rango de PKs de una ruta
 */
export const formatPKRange = (
  pkInitKm: number,
  pkInitMeter: number,
  pkEndKm: number,
  pkEndMeter: number
): string => {
  return `${formatPK(pkInitKm, pkInitMeter)} - ${formatPK(pkEndKm, pkEndMeter)}`;
};

/**
 * Formatea la distancia en kilómetros con decimales
 */
export const formatDistanceKm = (distanceMeters: number): string => {
  const km = distanceMeters / 1000;
  return `${km.toFixed(2)} km`;
};

/**
 * Distancia de un tramo en km (un decimal) — columna "Km" de la tabla de
 * tramos (UX 2026-07-07: valor de supervisión derivado de los PKs, junto a
 * "Duración"). Reusa `pkToMeters` para convertir cada PK (km, m) a metros
 * antes de restar. Em-dash si falta cualquiera de los 4 valores — el tramo
 * activo no tiene PK final todavía.
 */
export const formatSegmentKm = (
  pkInitKm?: number | null,
  pkInitMeter?: number | null,
  pkEndKm?: number | null,
  pkEndMeter?: number | null,
  /**
   * Trozos del tramo (multi-carretera 2026-08-17). 🔴 Con varios, la distancia
   * es la SUMA de lo recorrido en cada vía: restar los extremos mezclaría
   * kilometrajes de carreteras distintas y daría un número sin significado. Con
   * la C-25 y su ramal el error pasaría desapercibido —heredó la numeración—
   * pero en otra concesión sería un disparate.
   */
  roadStretches?: RoadStretch[] | null,
  /**
   * La carretera del tramo y las mallas cacheadas (multi-carretera / kilómetro
   * largo, 2026-08-26). Sin ellas el número se calcula como siempre; con ellas
   * se mide el asfalto, que es lo que arregla los tramos que cruzan un
   * kilómetro largo — medido en beta, `82+800 → 237+100` pintaba 154,3 km donde
   * el recorrido son 152,5.
   */
  opts?: {
    roadId?: string | null;
    meshesByRoadId?: Record<string, PkMeshVertex[]> | null;
  }
): string => {
  const meters = segmentMeters(
    {
      roadId: opts?.roadId,
      pkInitKm,
      pkInitMeter,
      pkEndKm,
      pkEndMeter,
      roadStretches: roadStretches ?? undefined,
    },
    opts?.meshesByRoadId
  );
  // Em-dash si falta algún PK: el tramo activo aún no tiene final.
  if (meters == null) return '—';
  return (meters / 1000).toFixed(1);
};

// ═══════════════════════════════════════════════════════════════════
// DATE/TIME UTILITIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Formatea la duración en formato legible (Xh Ymin / Xmin / Xs).
 * Usado por RouteControlPanel, Dashboard y SegmentList.
 */
export const formatDuration = (durationMs: number): string => {
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}min`;
  }
  if (minutes > 0) {
    return `${minutes}min`;
  }
  return `${seconds}s`;
};

/**
 * Formatea una duración como cronómetro HH:MM:SS.
 * Usado por el contador en vivo del timer activo.
 */
export const formatElapsedTime = (durationMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Duración de un tramo cerrado, formato "Xh Ym" — columna "Duración" de la
 * tabla de tramos (UX 2026-07-07: valor de supervisión, complementa "Km").
 * Em-dash para el tramo activo (sin `endDate`) — un elapsed-so-far cambiaría
 * en cada render sin un timer en vivo detrás, sería engañoso en una tabla.
 */
export const formatSegmentDuration = (
  startDate?: string | null,
  endDate?: string | null
): string => {
  if (!startDate || !endDate) return '—';
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return '—';
  }
  const totalMinutes = Math.round((end - start) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

/**
 * Calcula el elapsed time total del recorrido — Timer iteration 2026-04-15.
 *
 * Suma las duraciones de todos los timers (segments) del recorrido. Para el
 * timer activo usa `now` como endDate. Como las pausas son "gaps" entre timers
 * cerrados, quedan automáticamente excluidas del cómputo.
 *
 * Reemplaza al viejo `calculateRouteDuration + calculateTotalPauseTime +
 * calculateEffectiveWorkTime` que asumían un único start/end date en el
 * recorrido y una entidad `RoutePause` separada.
 */
export const calculateRouteElapsedMs = (
  route: { segments?: RouteSegment[] } | undefined,
  now: number = Date.now()
): number => {
  if (!route?.segments) return 0;
  return route.segments.reduce((total, s) => {
    if (!s.startDate) return total;
    const end = s.endDate ? new Date(s.endDate).getTime() : now;
    return total + Math.max(0, end - new Date(s.startDate).getTime());
  }, 0);
};

/**
 * Devuelve el timer activo del recorrido (el único con `endDate == null`),
 * o null si no hay ninguno.
 */
export const getActiveSegment = (
  route: { segments?: RouteSegment[] } | undefined
): RouteSegment | null => {
  if (!route?.segments) return null;
  return route.segments.find((s) => !s.endDate) ?? null;
};

/**
 * @deprecated Timer iteration 2026-04-15: usa `calculateRouteElapsedMs(route)`.
 * Mantiene la firma antigua por compatibilidad con código que aún no migra.
 */
export const calculateRouteDuration = (
  route: SurveillanceRoute
): number | null => {
  if (!route.registerInitDate) return null;
  return calculateRouteElapsedMs(route);
};

/**
 * @deprecated Timer iteration 2026-04-15: el modelo nuevo no tiene `RoutePause`.
 * Las pausas son segments cerrados con `pauseCause != null`; el elapsed time
 * calculado por `calculateRouteElapsedMs` ya las excluye implícitamente.
 * Este helper siempre devuelve 0.
 */
export const calculateTotalPauseTime = (_route: SurveillanceRoute): number => {
  return 0;
};

/**
 * @deprecated Timer iteration 2026-04-15: usa `calculateRouteElapsedMs(route)`.
 * En el modelo nuevo el elapsed time ya es "tiempo efectivo de trabajo" sin
 * necesidad de restar pausas.
 */
export const calculateEffectiveWorkTime = (
  route: SurveillanceRoute
): number | null => {
  return calculateRouteDuration(route);
};

// ═══════════════════════════════════════════════════════════════════
// SEGMENT (TIMER) UTILITIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Número total de timers (RouteSegments) del recorrido.
 */
export const getSegmentCount = (route: SurveillanceRoute): number => {
  return route.segments?.length || 0;
};

/**
 * Número de operarios únicos asignados al recorrido.
 * Timer iteration 2026-04-15: los operarios son del recorrido, no de los segments.
 */
export const getUniqueOperatorCount = (route: SurveillanceRoute): number => {
  return route.userIDs?.length ?? 0;
};

/**
 * True si todos los timers están cerrados (ninguno tiene `endDate == null`).
 * Útil para saber si el recorrido está listo para ser finalizado.
 */
export const areAllSegmentsCompleted = (route: SurveillanceRoute): boolean => {
  if (!route.segments || route.segments.length === 0) return false;
  return route.segments.every((segment) => !!segment.endDate);
};

// ═══════════════════════════════════════════════════════════════════
// RBAC HELPERS — Timer iteration 2026-04-15
// ═══════════════════════════════════════════════════════════════════

/**
 * Determina si el usuario puede operar el timer del recorrido
 * (iniciar / pausar / reanudar / finalizar / crear timer / cerrar timer).
 *
 * Reglas:
 * - El usuario tiene que ser rol Tablet.
 * - El usuario tiene que pertenecer a al menos un equipo asignado al recorrido
 *   (intersección no vacía entre `user.teamIds` y `route.teamIDs`).
 *
 * Coex, SuperAdmin y RoomTeam son supervisores: pueden ver, filtrar y navegar,
 * pero NO operan timers. Ver sección RBAC en REQUISITOS.
 *
 * El backend valida lo mismo server-side en el service (no como guard
 * NestJS — patrón canónico ROIS): desde 2026-10-22 abrir/cerrar tramo y
 * entregar pasan por `assertTabletOperatesRoute` (equipo + dueña de la
 * patrulla viva).
 */
/**
 * ¿El usuario que lo mira es el DUEÑO ACTUAL del timer de este recorrido?
 *
 * `SurveillanceRoute.timerOwnerId` es la tablet que tiene el cronómetro AHORA
 * — propiedad RECLAMABLE: el backend la reasigna al que RETOMA el RV (arrancar
 * / crear tramo). NO es `userId` (el CREADOR denormalizado, para el filtro
 * "Creador") ni `userIDs` (la CUADRILLA — operarios de carne y hueso, que ni
 * tienen por qué tener cuenta de tablet). Antes (#118) esto miraba `userId`,
 * conflando creador y dueño; se separó el 2026-07-30 al hacer la propiedad
 * reclamable (dueño ≠ creador tras un relevo entre tablets de la misma ÈXSER).
 */
export const isOwnSurveillanceRoute = (
  user: { id?: string } | undefined | null,
  route: { timerOwnerId?: string | null } | undefined | null
): boolean => {
  if (!user?.id || !route?.timerOwnerId) return false;
  return route.timerOwnerId === user.id;
};

/**
 * ¿Puede este usuario operar el timer del RV (iniciar/pausar/reanudar/finalizar
 * /crear-cerrar tramo)? Reglas:
 * - Rol Tablet + pertenecer a algún equipo del RV (intersección teamIDs).
 * - Propiedad RECLAMABLE (2026-07-30): un RV EN PATRULLA (`in-progress` O
 *   `paused`) pertenece a su `timerOwnerId` — la patrulla sigue VIVA aunque
 *   esté pausada (el dueño va a reanudar entre tramos), así que NINGÚN otro
 *   la opera. `paused` NO es "libre"; solo un RV TERMINADO se traspasa.
 *   La propiedad se asigna en 3 sitios (Modelo B, decisión usuario 2026-07-30):
 *   arrancar un RV `open` (`start`) y cada `createRouteSegment` → quien arranca
 *   se lo queda; y REABRIR un RV terminado → la TABLET que reabre SE LO QUEDA
 *   (relevo = la tablet que va a seguir es la que reabre), mientras que un
 *   GESTOR que reabre lo LIBERA (`timerOwnerId=null`). Así cada RV activo tiene
 *   normalmente un dueño claro. Un `paused` CON dueño (pausa a media patrulla o
 *   reabierto por tablet) está protegido; un `paused` SIN dueño (release de
 *   gestor, o legacy sin backfill) lo opera cualquier tablet del equipo.
 *
 * El backend valida lo mismo server-side (`assertTabletOperatesRoute`: equipo +
 * dueña de la patrulla viva, desde 2026-10-22; más la reasignación de
 * `timerOwnerId` en start/createSegment/reopen).
 */
export const canOperateTimer = (
  user:
    | { id?: string; rolActive?: string | null; teamIDs?: string[] }
    | undefined
    | null,
  route:
    | {
        teamIDs?: string[] | null;
        timerOwnerId?: string | null;
        state?: string | null;
      }
    | undefined
    | null
): boolean => {
  if (!user || !route) return false;
  if (user.rolActive !== Role.Tablet) return false;
  const userTeams = user.teamIDs ?? [];
  const routeTeams = route.teamIDs ?? [];
  if (userTeams.length === 0 || routeTeams.length === 0) return false;
  if (!routeTeams.some((t) => userTeams.includes(t))) return false;

  // Protege la patrulla VIVA (in-progress O paused): si tiene dueño y es otro,
  // no la toques. `paused` incluido a propósito — una patrulla pausada sigue
  // siendo de su dueño (va a reanudar); el relevo a otra tablet pasa por
  // TERMINAR + REABRIR, no por robar un RV pausado.
  if (
    (route.state === RouteState.IN_PROGRESS ||
      route.state === RouteState.PAUSED) &&
    route.timerOwnerId &&
    route.timerOwnerId !== user.id
  ) {
    return false;
  }
  return true;
};

/**
 * ¿Qué recorrido elige SOLO el cronómetro? (spec 2026-10-22-cronometro-otra-patrulla)
 *
 * Hasta el 2026-10-22 era «si en la lista solo hay uno, ese», sin mirar de
 * quién es. En la UTE patrullan dos tablets a la vez y la lista es de todo el
 * equipo: al entregar la suya, a la tablet le quedaba el de la otra patrulla,
 * se lo quedaba y la barra enseñaba su tiempo como si fuera el propio.
 *
 * - Tablet: solo si la lista tiene EXACTAMENTE UN recorrido y es suyo
 *   (`isOwnSurveillanceRoute`). El de otra patrulla y el libre (sin dueño) se
 *   eligen a mano.
 *
 *   🔴 Elegir el propio ENTRE VARIOS se retiró (Ruling 6): sin P1 (la
 *   pregunta «¿es de otro día?», aún no en main) el «suyo» podría ser uno
 *   VIEJO sin entregar (Su.3827/3849/3898) estando la otra patrulla activa, y
 *   la barra enseñaría el tiempo de un turno ajeno. En la pila de P1, que sí
 *   pregunta por los de otro turno, se puede recuperar.
 * - Quien solo mira (Coex, Sala, Capataz, SuperAdmin): la regla de siempre.
 *
 * El otro camino que elige solo —el recorrido que esta tablet empieza a
 * grabar (`capturingRoute` en `TimerOverlay`)— no pasa por aquí.
 */
export const autoSelectableRouteId = (
  user: { id?: string; rolActive?: string | null } | undefined | null,
  activeRoutes: ReadonlyArray<{ id: string; timerOwnerId?: string | null }>
): string | undefined => {
  if (user?.rolActive !== Role.Tablet) {
    return activeRoutes.length === 1 ? activeRoutes[0].id : undefined;
  }
  return activeRoutes.length === 1 &&
    isOwnSurveillanceRoute(user, activeRoutes[0])
    ? activeRoutes[0].id
    : undefined;
};

/**
 * ¿Tiene ESTE aparato un tramo abierto? Es lo que decide si se le puede
 * interrumpir: el aviso de actualización y el guardián del permiso de fotos
 * esperan a que no lo haya.
 *
 * 🔴 Hasta el 2026-09-30 se miraba «el recorrido SELECCIONADO en el cronómetro
 * tiene tramo abierto». El cronómetro selecciona recorridos del EQUIPO, así
 * que fallaba por los dos lados:
 * - un capataz u otra tablet que solo MIRA el recorrido se quedaba sin avisos
 *   mientras la patrulla tuviera un tramo abierto — toda la jornada;
 * - la tablet que patrulla, mirando OTRO recorrido en el desplegable, perdía de
 *   vista su propio tramo y podía recibir el aviso en mitad de la captura.
 */
export const hasOwnOpenSegment = ({
  user,
  selectedRoute,
  selectedRouteHasOpenSegment,
  capturingRouteId,
}: {
  user: Parameters<typeof canOperateTimer>[0];
  selectedRoute:
    | (NonNullable<Parameters<typeof canOperateTimer>[1]> & { id?: string })
    | null
    | undefined;
  /** Tramo abierto del recorrido seleccionado, fusionado con la cola offline. */
  selectedRouteHasOpenSegment: boolean;
  /** El recorrido que ESTA tablet está haciendo (`capturingRoute`). */
  capturingRouteId: string | null | undefined;
}): boolean => {
  // Tramo abierto en un recorrido que ESTE usuario opera: es el suyo. Cubre
  // también el tramo recién abierto sin red, que solo existe en la cola.
  if (selectedRouteHasOpenSegment && canOperateTimer(user, selectedRoute)) {
    return true;
  }
  // Graba un recorrido pero está mirando otro: su tramo no se ve desde aquí.
  // Lado seguro, como si estuviera abierto; el aviso sale en cuanto vuelve a
  // su recorrido o lo termina.
  if (capturingRouteId && selectedRoute?.id !== capturingRouteId) return true;
  return false;
};

// ═══════════════════════════════════════════════════════════════════
// CREW VALIDATION — NewRoute wizard (V4 FR-3.4, Task 3 2026-07-07)
// ═══════════════════════════════════════════════════════════════════

/**
 * V4 FR-3.4: validaciones bloqueantes de la tab Operarios del wizard "Nou
 * recorregut" — mínimo 1 operario, mínimo 1 vehículo y conductor asignado en
 * todo vehículo seleccionado. Mismos mensajes que el validador final del
 * wizard de actuaciones (`utils/formValidation.ts`).
 *
 * Extraída de `NewRoute/index.tsx` (Task 3 del refactor de patrones RV,
 * 2026-07-07) a función pura — se usa tanto en la validación del submit
 * (`onSubmit`) como en el recálculo EN VIVO (`onCrewChange`).
 */
export const computeCrewErrors = (crew: {
  operators: number;
  vehicles: number;
  vehiclesWithoutDriver: number;
}): string[] => {
  const errs: string[] = [];
  if (crew.operators === 0) {
    errs.push(i18n.t('validation:operators.atLeastOne'));
  }
  if (crew.vehicles === 0) {
    errs.push(i18n.t('validation:vehicles.atLeastOne'));
  } else if (crew.vehiclesWithoutDriver > 0) {
    errs.push(i18n.t('validation:vehicles.driverRequired'));
  }
  return errs;
};

// ═══════════════════════════════════════════════════════════════════
// TABLE ADAPTER — SurveillanceRoute → Order-compatible row
// ═══════════════════════════════════════════════════════════════════

type NamedRef = { id: string; name: string };

type FiltersDataOptions = {
  capitols?: NamedRef[];
  concessions?: NamedRef[];
  subCapitols?: NamedRef[];
  classifications?: Array<NamedRef & { classificationCode?: number }>;
  teams?: NamedRef[];
  predefinedOrders?: NamedRef[];
  // V4 iter7 (2026-05-06): roads se incluyen en filtersDataOptions para que
  // el adapter resuelva `roadIDs[0]` → road.name (antes la columna `road` de
  // las listings mostraba el Mongo ObjectId crudo).
  roads?: NamedRef[];
  // 2026-07-12: usuarios para el dropdown Operarios del listado RV
  // (showOperatorsFilter — filterAndSortOperators filtra por userType).
  users?: Array<{
    id: string;
    name?: string;
    surName?: string;
    isActive?: boolean;
    roles?: string[];
    userType?: string;
  }>;
};

// V4 (2026-04-21): mapping del `state` del RV → SpecialTag variant.
// El `state` del RV vive en el mismo campo que el resto de Orders (enum unificado).
// 2026-07-20: in-progress/paused ya tienen variant + label propios en
// SpecialTag — antes se mapeaban a work-in-progress/pending-work y el operario
// veía "Pendiente de trabajo" en un RV pausado. 2026-08-05: además tienen
// TEXTO y COLOR propios ("En progreso", blueRouteInProgress); hasta entonces
// in-progress copiaba literalmente los de work-in-progress.
const ROUTE_STATE_TO_TAG_VARIANT: Record<string, string> = {
  open: 'open',
  'in-progress': 'in-progress',
  paused: 'paused',
  'pending-validation': 'pending-validation',
  end: 'end',
  annulled: 'annulled',
};

export const mapRouteStateToTagVariant = (state?: string): string => {
  if (!state) return 'default';
  return ROUTE_STATE_TO_TAG_VARIANT[state] ?? 'default';
};

const lookupName = (
  options: NamedRef[] | undefined,
  id: string | undefined
): string => {
  if (!id || !options) return '';
  return options.find((opt) => opt.id === id)?.name ?? '';
};

/**
 * V4 FR-1.6/1.8 (2026-05-06) — adapter pragmático SR → row shape Order-like.
 *
 * El refactor canónico (eliminar el adapter y usar columnas nativas RV)
 * requiere modificar `InputRadioTagTable`/`CheckBoxTagTable` para aceptar un
 * shape distinto al genérico Order — alto riesgo en otros módulos.
 * Como compromiso, tras iter4 (2026-05-06) las tablas exponen
 * `hiddenColumnIds`/`hiddenFilterIds` + `showRouteNameColumn`, y el adapter:
 *
 *  1. Construye el ROID canónico desde `indexExpedition`/`indexType` cuando
 *     la Order no llega a tiempo (antes caía a `sr.id.slice(0,8)` — hash crudo).
 *  2. Devuelve `''` para los campos no aplicables (capitol/subCapitol/track/
 *     direction/etc.) — quedan ocultos por `hiddenColumnIds`. Mantenemos los
 *     campos en el output para no romper el typing del row.
 */
/**
 * Mapeo unificado sentido→key i18n (fix ronda 4 auditoría 2026-07-03).
 * Los modales del timer envían el vocabulario de NewWorkOrderForm
 * (`top|down|both|sense especificar`), pero datos legacy del backend pueden
 * traer `ascending|descending|bottom|up|none`. Antes cada consumidor
 * (TimerOverlay, SegmentList, PDF) tenía su propio mapeo parcial y el
 * overlay mostraba el valor crudo ("top") o computaba mal el nombre.
 * Las 4 keys existen en pages.json (ca/es/en) — NO crear keys nuevas.
 */
const ASCENDING_DIRECTIONS = ['ascending', 'top', 'up'];
const DESCENDING_DIRECTIONS = ['descending', 'bottom', 'down'];

export const directionNameKey = (direction?: string | null): string | null => {
  if (!direction) return null;
  if (ASCENDING_DIRECTIONS.includes(direction))
    return 'pages:surveillance.directionAscending';
  if (DESCENDING_DIRECTIONS.includes(direction))
    return 'pages:surveillance.directionDescending';
  if (direction === 'both') return 'pages:surveillance.directionBoth';
  return 'pages:surveillance.directionUnspecified';
};

/**
 * Display UNIFICADO de la columna "Sentido" en TODAS las tablas de listado.
 * Fuente única de verdad para el formato "Texto (símbolo)" — usado por
 * `selectAllOrders` (listados de OT/campañas/históricos y, por reúso de ese
 * selector, las tablas de los modales relacionar/transferir). Coherente con
 * el patrón ya existente `Lleida (↓)` / `Girona (↑)`:
 *
 *   descendente (down/descending/bottom) → `${roadDirections[0]} (↓)`
 *   ascendente  (top/ascending/up)       → `${roadDirections[último]} (↑)`
 *   ambos       (both)                   → `Ambos sentidos (↕)`
 *   sin especificar / vacío              → `Sin especificar`
 *
 * Si la carretera no trae `directions` (dato incompleto) cae al label genérico
 * `Sentido descendente/ascendente` en vez de un vacío — antes `both` mostraba
 * solo el símbolo `↕` sin texto y `sin especificar` no mostraba NADA (feedback
 * usuario 2026-07-21). Reutiliza claves i18n existentes
 * (`common:workOrder.unspecified`, `pages:surveillance.direction*`) — NO crea
 * ninguna key nueva. Vocabulario legacy normalizado vía los sets
 * ASCENDING/DESCENDING (mismo criterio que `directionNameKey`).
 */
export const formatDirectionCellLabel = (
  direction?: string | null,
  roadDirections?: (string | null | undefined)[] | null
): string => {
  const dirs = roadDirections ?? [];
  if (!direction || direction === UNSPECIFIC) {
    return i18n.t('common:workOrder.unspecified');
  }
  if (DESCENDING_DIRECTIONS.includes(direction)) {
    return `${dirs[0] || i18n.t('pages:surveillance.directionDescending')} (↓)`;
  }
  if (ASCENDING_DIRECTIONS.includes(direction)) {
    const last = dirs[dirs.length - 1];
    return `${last || i18n.t('pages:surveillance.directionAscending')} (↑)`;
  }
  if (direction === 'both') {
    return `${i18n.t('pages:surveillance.directionBoth')} (↕)`;
  }
  // Valor desconocido no mapeable — se devuelve crudo (comportamiento previo).
  return direction;
};

/**
 * Normaliza el vocabulario legacy (`ascending|up` / `descending|bottom`) al
 * vocabulario CANÓNICO que `GeoPositionRoad.direction` almacena en BD
 * (verificado en datos reales: SOLO existen los valores `top`/`down`, nunca
 * `ascending`). Bug 2026-07-08: `MapWithSegments.loadSegmentCoordinates`
 * pasaba `segment.direction` crudo al filtro EXACTO de
 * `geoPositionRoads(direction: ...)` — un tramo legacy con
 * `direction: 'ascending'` no encontraba NINGÚN punto (0 resultados), así
 * que ese tramo se quedaba sin coordenadas y su marcador de pausa
 * desaparecía del mapa aunque la causa siguiera listada en la leyenda
 * (`pauseCausesPresent`, que lee solo datos del segmento, no coordenadas).
 */
export const normalizeDirectionForQuery = (
  direction?: string | null
): string | undefined => {
  if (!direction) return undefined;
  if (ASCENDING_DIRECTIONS.includes(direction)) return 'top';
  if (DESCENDING_DIRECTIONS.includes(direction)) return 'down';
  return direction;
};

/**
 * Quita una ocurrencia LÍDER de `leadWord` (case-insensitive) + espacio del
 * inicio de `label`, y capitaliza el resto — UX 2026-07-07: la celda
 * "Sentido" de la tabla de tramos repetía la palabra de la cabecera
 * ("Sentido ascendente" bajo la columna "Sentido"). Si `label` NO empieza
 * por `leadWord` se devuelve sin tocar — así los valores legacy crudos
 * (`directionName === 'top'`) o los labels en otro orden de palabras (inglés
 * "Ascending direction") quedan intactos, sin falsos positivos.
 */
export const stripLeadingWord = (label: string, leadWord: string): string => {
  const trimmedWord = leadWord.trim();
  if (!trimmedWord) return label;
  const escaped = trimmedWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`^${escaped}\\s+`, 'i');
  const stripped = label.replace(pattern, '');
  if (stripped === label || stripped.length === 0) return label;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

/** Invierte el sentido en el vocabulario de los modales (`top`↔`down`). */
export const invertDirection = (direction?: string | null): string => {
  if (direction && ASCENDING_DIRECTIONS.includes(direction)) return 'down';
  if (direction && DESCENDING_DIRECTIONS.includes(direction)) return 'top';
  return direction ?? 'top';
};

/**
 * Nombre visible del carril a partir del value técnico (`right`/`left`/...).
 * Delega en el módulo único del catálogo, así que traduce al idioma activo.
 * Sin esto el overlay y la fila del tramo mostraban el value crudo en inglés
 * ("right"). Se conserva el nombre `laneName` para no tocar a sus tres
 * consumidores: overlay del cronómetro, fila del tramo e informe del RV.
 */
export const laneName = (lane?: string | null): string => laneLabel(lane);

// ═══════════════════════════════════════════════════════════════════
// FR-9.5 / I2 (QA IA 2026-07-13) — contexto ?fromSegment resistente a F5.
// El wizard encadenado entra con `?fromSegment=<segmentEntityId>` en la URL,
// pero un F5 a mitad de wizard la pierde (el router normaliza + el GET sin
// actionData rebota al detalle) y el auto-link se esfumaba en silencio
// (tramo huérfano). La key se escribe al NAVEGAR al chain con SCOPE del RV
// (`routeId|segmentId`) y se CONSUME (una vez) en el saveData que crea la
// actuación, SOLO si el RV coincide — así el resume tras F5 (re-entrar por
// "Crear incidencia" en el mismo RV) conserva el auto-link, y un chain
// abandonado nunca contamina actuaciones de OTRO recorrido.
// ═══════════════════════════════════════════════════════════════════

const SR_FROM_SEGMENT_PENDING_KEY = 'sr-from-segment-pending';

export const rememberFromSegment = (
  routeId: string,
  segmentEntityId: string
): void => {
  try {
    sessionStorage.setItem(
      SR_FROM_SEGMENT_PENDING_KEY,
      `${routeId}|${segmentEntityId}`
    );
  } catch {
    /* sessionStorage no disponible — el auto-link cae al param de la URL */
  }
};

export const consumeFromSegment = (routeId: string): string | null => {
  try {
    const v = sessionStorage.getItem(SR_FROM_SEGMENT_PENDING_KEY);
    sessionStorage.removeItem(SR_FROM_SEGMENT_PENDING_KEY);
    if (!v) return null;
    const [storedRouteId, segmentId] = v.split('|');
    return storedRouteId === routeId && segmentId ? segmentId : null;
  } catch {
    return null;
  }
};

export const buildRoidFallback = (sr: SurveillanceRoute): string => {
  const exp = sr.indexExpedition;
  const idx = sr.indexType ?? 0;
  if (typeof exp === 'number' && Number.isFinite(exp)) {
    return `Su.${exp.toString().padStart(7, '0')}.${idx.toString().padStart(4, '0')}.02`;
  }
  return '';
};

/**
 * Nombre con el que se muestra un recorrido en la interfaz.
 *
 * Cadena de respaldo: nombre → ROID canónico → etiqueta i18n. El último
 * escalón existe para no filtrar el ObjectId en pantalla.
 *
 * Vive aquí y no dentro del `TimerOverlay` porque lo usan DOS sitios del mismo
 * panel —el desplegable de recorridos y el enlace a la ficha— y ya se sabe
 * cómo acaban las reglas duplicadas en este repositorio: divergen. Que el
 * enlace no dependa de `sr.name` a secas no es cosmético: un recorrido sin
 * nombre no pintaba nada, así que no había nada que pulsar para llegar a él.
 */
export const routeDisplayName = (sr: SurveillanceRoute): string =>
  sr.name || buildRoidFallback(sr) || i18n.t('pages:surveillance.unnamedRoute');

export const adaptSurveillanceRouteToOrder = (
  sr: SurveillanceRoute,
  filtersDataOptions: FiltersDataOptions,
  order?: {
    id?: string;
    description?: string;
    roid?: string;
    registerEndDate?: string | null;
    userId?: string | null;
  }
) => {
  // Multi-carretera (2026-08-17): con VARIAS carreteras, un solo par de PK es
  // falso — el escalar apunta a la primera. Cada celda apila un valor por
  // carretera, en el mismo orden que la columna «Carretera», para que la fila
  // se lea en horizontal. Con UNA sola, todo queda byte a byte como antes.
  const roadRanges = resolveRouteRoadRanges(sr);
  const isMultiRoad = (sr.roadIDs?.length ?? 0) > 1;

  const pkInit = isMultiRoad
    ? roadRanges.map((r) => formatPK(r.pkInitKm, r.pkInitMeter)).join('\n')
    : sr.pkInitKm !== undefined
      ? formatPK(sr.pkInitKm, sr.pkInitMeter ?? 0)
      : '';
  const pkEnd = isMultiRoad
    ? roadRanges.map((r) => formatPK(r.pkEndKm, r.pkEndMeter)).join('\n')
    : sr.pkEndKm !== undefined
      ? formatPK(sr.pkEndKm, sr.pkEndMeter ?? 0)
      : '';

  // V4 post-refactor 2026-04-22: `createdAt`/`updatedAt` eliminados del SR.
  // `creationDate`/`registerInitDate` son denormalizados desde la Order asociada.
  const creationDate = sr.creationDate ?? '';
  const registerInitDate = sr.registerInitDate ?? '';

  return {
    id: sr.id,
    // BUG paginación RV (2026-07-12, cazado en QA IA): los handlers de
    // paginación de las tablas compartidas pivotan sobre
    // `row.genericOrCampaignOrderID` y sin este campo el guard nunca
    // despachaba el cursor — el indicador avanzaba de página pero la tabla
    // repetía la página 1. Nadie lo vio antes porque el listado RV no
    // superaba las 25 filas. El cursor del backend RV es el id del propio
    // SurveillanceRoute.
    genericOrCampaignOrderID: sr.id,
    // Banderitas (spec 2026-10-22 § 8.4): la bandera marca la ORDER gemela del
    // recorrido, no el SurveillanceRoute (`id` es el del recorrido porque es
    // también su cursor). Si la Order no se cargó, su id sale del propio
    // recorrido. Sin ninguno de los dos, la celda queda sin botón.
    orderId: order?.id ?? sr.orderIDs?.[0],
    // V4 FR-1.8 — fallback al ROID canónico construido desde el schema RV
    // si la Order no se carga a tiempo (antes era `sr.id.slice(0,8)` —
    // hash crudo que confundía a los usuarios).
    roid: order?.roid || buildRoidFallback(sr) || sr.id || '',
    name: sr.name || '',
    // state must be a SpecialTag variant string (not an object)
    state: mapRouteStateToTagVariant(
      typeof sr.state === 'string' ? sr.state : String(sr.state ?? '')
    ),
    specialTag: typeof sr.state === 'string' ? sr.state : '',
    // Prevención P3 (2026-10-15): la tabla pinta la marca bajo el estado.
    shiftWarning: sr.shiftWarning ?? null,
    creationDate,
    date: creationDate,
    registerInitDate,
    // Paridad #72 — columna "Fecha final" del listado: para un RV la fecha
    // fin es la `registerEndDate` de su Order gemela (la escribe finish()),
    // no la derivada de actuaciones. Sin este mapeo la columna salía "—"
    // también en RVs terminados.
    derivedEndDate: order?.registerEndDate ?? '',
    // V4 post-refactor 2026-04-22: `description` vive en la Order asociada.
    description: order?.description ?? '',
    // V4 FR-4.2: track/direction/margin/lane no aplican al RV — quedan ocultas
    // vía `hiddenColumnIds` de la tabla. Mantenemos el campo en el shape
    // para no romper el typing del row.
    track: '',
    direction: '',
    margin: '',
    lane: '',
    pkInit,
    pkEnd,
    // id → name lookups against filtersDataOptions
    concession: lookupName(
      filtersDataOptions.concessions,
      sr.concessionIDs?.[0]
    ),
    capitol: lookupName(filtersDataOptions.capitols, sr.capitolIDs?.[0]),
    subCapitol: lookupName(
      filtersDataOptions.subCapitols,
      sr.subCapitolIDs?.[0]
    ),
    predefinedOrder: lookupName(
      filtersDataOptions.predefinedOrders,
      sr.predefinedOrderIDs?.[0]
    ),
    team: lookupName(filtersDataOptions.teams, sr.teamIDs?.[0]),
    // Columna Operarios (feedback usuario 2026-07-14): en RV los operarios
    // van ligados al RECORRIDO (RV.userIDs), no a sus actuaciones — la tabla
    // del listado los muestra resueltos a nombre. `users` llega en
    // filtersDataOptions desde el loader (mismo dataset que el filtro).
    operators: (sr.userIDs ?? [])
      .map((uid) => {
        const u = filtersDataOptions.users?.find((x) => x.id === uid);
        return u ? `${u.name ?? ''} ${u.surName ?? ''}`.trim() : '';
      })
      .filter(Boolean)
      .join(', '),
    // Columna "Creado por" (2026-07-17): el creador es el `userId` de la
    // Order gemela (Tipo C, NEW_ORDER ya lo trae) — DISTINTO de los
    // operarios (RV.userIDs, arriba). Se resuelve a nombre contra el mismo
    // dataset `users` del loader (patrón idéntico a `operators`); la
    // columna pinta '—' si queda vacío.
    creatorName: (() => {
      if (!order?.userId) return '';
      const u = filtersDataOptions.users?.find((x) => x.id === order.userId);
      return u ? `${u.name ?? ''} ${u.surName ?? ''}`.trim() : '';
    })(),
    // V4 iter7: si los loaders no han suministrado `roads` en
    // filtersDataOptions todavía, dejamos vacío en lugar del Mongo ObjectId
    // crudo (mejor UX que un hash de 24 caracteres).
    // Multi-carretera (2026-08-17): TODAS, no solo la primera — que es lo que
    // hacía y dejaba media verdad en la tabla (el Excel ya las juntaba). Una
    // por línea, alineadas con las celdas de PK de arriba.
    road: (sr.roadIDs ?? [])
      .map((id) => lookupName(filtersDataOptions.roads, id))
      .filter(Boolean)
      .join('\n'),
  };
};

/**
 * Id de la Order que marca la bandera de una fila de recorrido (spec 2026-10-22
 * § 8.4): el `orderId` que pone `adaptSurveillanceRouteToOrder`, nunca el `id`
 * de la fila (que es el del SurveillanceRoute). Lo pasan como `getFlagOrderId`
 * los dos listados de recorridos; fuera de los componentes para que la
 * referencia sea estable y la tabla no rehaga sus columnas en cada render.
 */
export const getSurveillanceRouteFlagOrderId = (row: {
  orderId?: string;
}): string | undefined => row.orderId || undefined;

/**
 * Adapts a full loader-data shape (SurveillanceRoute list + metadata) to what
 * InputRadioTagTable / CheckBoxTagTable expects as `data` prop.
 */
export const adaptSurveillanceRoutesLoaderData = (loaderData: {
  surveillanceRoutes?: SurveillanceRoute[];
  filtersDataOptions?: FiltersDataOptions;
  totalCount?: number;
  pageInfo?: any;
  loading?: boolean;
  roleActive?: any;
  // Mapa srId → Order cargado via `selectOrdersForSurveillanceRoutes` (patrón
  // canónico entidad doble — la description/roid/notes viven en la Order).
  ordersBySurveillanceRouteId?: Record<
    string,
    {
      id?: string;
      description?: string;
      roid?: string;
      registerEndDate?: string | null;
      userId?: string | null;
    }
  >;
}) => {
  const filtersDataOptions = loaderData.filtersDataOptions ?? {};
  const ordersMap = loaderData.ordersBySurveillanceRouteId ?? {};
  const genericOrders = (loaderData.surveillanceRoutes || []).map((sr) =>
    adaptSurveillanceRouteToOrder(sr, filtersDataOptions, ordersMap[sr.id])
  );
  return {
    genericOrders: genericOrders as any,
    filtersDataOptions,
    totalCount: loaderData.totalCount,
    pageInfo: loaderData.pageInfo,
    loading: loaderData.loading,
    roleActive: loaderData.roleActive,
  };
};

/**
 * P15 rework #3 (2026-07-09) — adapter TRAMO (`RouteSegment`) → row shape
 * Order-like, MISMO patrón que `adaptSurveillanceRouteToOrder` (arriba):
 * campos no aplicables al dominio quedan en `''` y se ocultan vía
 * `hiddenColumnIds` en la tabla compartida que consume el resultado
 * (`InputRadioTagTable`, reusada TAL CUAL por `RelateSegmentModal` — no se
 * crea ninguna tabla hermana nueva; los 2 intentos previos que sí lo hacían
 * — `RelateSegmentRow` v1 y `RouteSegmentTable` v2 — fueron rechazados por
 * el usuario).
 *
 * `RouteSegment` no expone ni su SR propietaria por fila (comentario de
 * `ROUTE_SEGMENT_FRAGMENT`: "sin surveillanceRouteId — via
 * Order.parentExpeditionOrderIDs") ni su propio `roid`/`description` (viven
 * en la Order asociada, `segment.orderIDs[0]`) — el consumidor (el modal)
 * resuelve ambos de forma batched (mismo patrón `GET_ORDERS_BY_IDS_SERVER`
 * que usa `RouteDetailed/loader.ts#resolveOrderRoid`) y los pasa ya
 * resueltos vía los parámetros `routeName`/`order`.
 *
 * `state`/`specialTag` quedan en `''`: `pauseCause` ('basic'/'action'/
 * 'external-call') no tiene variant de `SpecialTag` — es un enum cerrado de
 * estados de Order/workflow (ver comentario de `PauseCauseTag`, styles.ts
 * ahora eliminado junto con `RouteSegmentTable`) — la columna "Estat" se
 * oculta vía `hiddenColumnIds` en el consumidor, este valor nunca se
 * renderiza.
 */
export const adaptSegmentToOrder = (
  segment: RouteSegment,
  routeName: string,
  order?: { roid?: string; description?: string },
  /** Nombre de la vía donde acabó el tramo (columna Carretera, 2026-09-23). */
  roadName?: string
) => {
  const pkInit = formatPK(segment.pkInitKm, segment.pkInitMeter);
  const pkEnd =
    segment.pkEndKm != null && segment.pkEndMeter != null
      ? formatPK(segment.pkEndKm, segment.pkEndMeter)
      : '';
  // Igual que SR: `registerInitDate` es el campo denormalizado desde la
  // Order del tramo; fallback a `startDate` (el timer propio del segmento)
  // para datos legacy sin denormalizar.
  const registerInitDate = segment.registerInitDate || segment.startDate || '';

  return {
    id: segment.id,
    roid: order?.roid || '—',
    name: routeName,
    state: '',
    specialTag: '',
    creationDate: registerInitDate,
    date: registerInitDate,
    registerInitDate,
    // Columna "Fecha final" (2026-08-11). Salía SIEMPRE "—" porque nadie
    // rellenaba este campo, y sin embargo el dato existe y es exacto: estas
    // filas son tramos CERRADOS —esa es la condición para estar en la bandeja—
    // y `endDate` es la hora a la que el operario los cerró, la misma que la
    // ficha del tramo pinta como "Hora final".
    derivedEndDate: segment.endDate ?? '',
    // La descripción de un TRAMO es su `pauseCauseDetail`, y SOLO eso — el
    // mismo dato que pintan la tabla de Tramos, el informe y la tarjeta del
    // tramo. Estas filas son tramos cerrados PIDIENDO algo (causa
    // `external-call` o `action`), así que lo que sala necesita para
    // identificar la incidencia es el motivo del cierre.
    //
    // 2026-08-11 (decisión del usuario): se retira el respaldo a la
    // `description` de la Order gemela. Esa «descripción de arranque» está
    // DEPRECADA para tramos —dejó de pedirse el 2026-08-10— y mantenerla aquí
    // dejaba esta columna leyendo un campo distinto del que leen las otras tres
    // superficies. Una sola fuente: lo que se escribe al cerrar el tramo.
    description: segment.pauseCauseDetail?.trim() || '',
    track: '',
    direction: '',
    margin: '',
    lane: '',
    pkInit,
    pkEnd,
    concession: '',
    capitol: '',
    subCapitol: '',
    predefinedOrder: '',
    team: '',
    road: roadName ?? '',
  };
};

// ═══════════════════════════════════════════════════════════════════
// Multi-carretera: el rango de PK va SIEMPRE pegado a su carretera
// ═══════════════════════════════════════════════════════════════════

/**
 * Con varias carreteras NO existe «el rango del recorrido», y presentarlo como
 * si existiera es lo que hacían la cabecera, la ficha del informe, la tabla del
 * listado y el Excel: enseñaban el par `pkInit`/`pkEnd` escalar, que apunta a la
 * PRIMERA carretera, bajo un título que hablaba del recorrido entero.
 *
 * Fundirlos falla por tres motivos distintos, y conviene tenerlos a mano porque
 * la tentación de hacer un min/max vuelve cada vez:
 *   1. los números no son comparables — el 235+000 de la C-25 y el de su ramal
 *      C-25z son dos sitios a 874 m, que es la premisa de toda la feature;
 *   2. un min/max daría un par que no existe en NINGUNA vía;
 *   3. el orden es arbitrario — un ramal puede numerar por encima o por debajo
 *      de la troncal, y dos carreteras que se solapan ni siquiera tienen un
 *      «antes» y un «después».
 *
 * De ahí este helper: la única forma correcta de enseñar los PK de un recorrido
 * es una pareja POR CARRETERA. Quien pinte rangos, que los pida aquí.
 */
export type RouteRoadRangeResolved = {
  roadId: string;
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
};

type RouteRangeSource = {
  roadIDs?: string[] | null;
  roadRanges?: RouteRoadRangeInput[] | null;
  pkInitKm?: number;
  pkInitMeter?: number;
  pkEndKm?: number;
  pkEndMeter?: number;
};

/**
 * El rango de cada carretera del recorrido, en el orden en que están declaradas.
 *
 * Tres fuentes, por orden de autoridad:
 *   1. el rango declarado en `roadRanges` (lo que tecleó el operario);
 *   2. para la PRIMERA carretera, los `pkInit`/`pkEnd` escalares del recorrido
 *      — son suyos por definición y es lo que tienen TODOS los recorridos
 *      anteriores a multi-carretera, así que esto es lo que evita el backfill;
 *   3. los extremos declarados de la propia vía (`roadRangesById`).
 *
 * Sin el tercer escalón, un ramal sin rango propio heredaría el kilometraje de
 * la troncal y saldría un tramo de vía arbitrario. Una carretera que no resuelva
 * por ninguna de las tres se omite: mejor no decir nada que decir un número
 * inventado.
 */
export const resolveRouteRoadRanges = (
  route: RouteRangeSource | null | undefined,
  roadRangesById: Record<string, PkInterval> = {}
): RouteRoadRangeResolved[] => {
  if (!route) return [];
  return (route.roadIDs ?? []).flatMap((roadId, index) => {
    const declared = (route.roadRanges ?? []).find((r) => r.roadId === roadId);
    // `declared` ya trae su `roadId`; el spread manda.
    if (declared) return [{ ...declared }];
    if (
      index === 0 &&
      route.pkInitKm !== undefined &&
      route.pkInitMeter !== undefined &&
      route.pkEndKm !== undefined &&
      route.pkEndMeter !== undefined
    ) {
      return [
        {
          roadId,
          pkInitKm: route.pkInitKm,
          pkInitMeter: route.pkInitMeter,
          pkEndKm: route.pkEndKm,
          pkEndMeter: route.pkEndMeter,
        },
      ];
    }
    const own = roadRangesById[roadId];
    return own ? [{ roadId, ...own }] : [];
  });
};

/**
 * «C-25 (82+800 → 164+000)» — UNA vía con su rango.
 *
 * Es la unidad del formato canónico: la usan tanto la fila «Carretera» del
 * recorrido (unida por comas, ver abajo) como la columna del catálogo de
 * plantillas de Gestión (una línea por vía). Vive aquí, y no copiada en cada
 * pantalla, porque la misma información leída de cuatro formas distintas es
 * justo lo que este formato vino a arreglar.
 *
 * Sin rango devuelve solo el nombre: es el caso degradado, no un error.
 */
export const formatRoadWithRange = (
  roadName: string,
  range: RouteRoadRangeResolved | undefined
): string => {
  if (!range) return roadName;
  const init = formatPK(range.pkInitKm, range.pkInitMeter);
  const end = formatPK(range.pkEndKm, range.pkEndMeter);
  return `${roadName} (${init} → ${end})`;
};

/**
 * «C-25 (82+800 → 235+800), C-25z (234+000 → 237+100)» para la fila «Carretera»
 * de la cabecera y de la ficha del informe.
 *
 * 🔴 Con UNA sola carretera devuelve solo su NOMBRE, sin rango: ese caso ya
 * tiene sus filas «PK Inicio» y «PK Fin» al lado y repetirlo sería ruido. O
 * sea: en un recorrido de una carretera esto es exactamente lo de siempre.
 */
export const formatRoadsWithRanges = (
  roadIds: string[] | null | undefined,
  roadNamesById: Record<string, string>,
  ranges: RouteRoadRangeResolved[]
): string => {
  const ids = roadIds ?? [];
  // Una carretera que no resuelva cae a su id, en contra de la convención de
  // «nunca un ObjectId crudo en pantalla» que sigue la columna del listado.
  // Aquí es a propósito y solo aplica al caso degradado: omitirla dejaría un
  // recorrido de dos carreteras diciendo que tiene una, que es EXACTAMENTE el
  // fallo que este formato viene a arreglar. Un identificador feo avisa; una
  // carretera desaparecida, no.
  const names = ids.map((id) => roadNamesById[id] || id);
  if (ids.length <= 1) return names.join(', ');

  return ids
    .map((id, index) =>
      formatRoadWithRange(
        names[index],
        ranges.find((r) => r.roadId === id)
      )
    )
    .join(', ');
};
// NAVEGACIÓN — ver una actuación SIN salirse del recorrido
// ═══════════════════════════════════════════════════════════════════

/**
 * Id del recorrido cuando la pantalla actual vive DENTRO de él.
 *
 * Mismo criterio que ya usa `ActionSummary` para calcular su salida: el id
 * sale de la URL, así que solo acierta —y solo debe acertar— mientras estamos
 * bajo `/surveillance-route/:routeId/...`. Fuera de ahí devuelve `null` y el
 * llamante cae a la ruta global.
 *
 * Se exige un ObjectId de 24 hex a propósito: los segmentos hermanos
 * ESTÁTICOS del módulo (`actions`, `multimedia-list`, `new-route`, …) no son
 * un recorrido y no deben producir rutas anidadas.
 */
export const getSurveillanceRouteIdFromPath = (
  pathname: string
): string | null =>
  pathname.match(/^\/surveillance-route\/([0-9a-fA-F]{24})(?:\/|$)/)?.[1] ??
  null;

/**
 * Ruta del resumen de una actuación: dentro del recorrido si estamos en uno,
 * y la global si no.
 *
 * Crear una actuación desde el recorrido se quedaba dentro pero VERLA saltaba
 * a `/work-orders/...`, con migas de Órdenes de trabajo — el operario perdía
 * el hilo y solo podía volver hacia atrás (reporte tablet 2026-08-15).
 */
export const buildActionSummaryPath = (
  actionId: string,
  surveillanceRouteId?: string | null
): string =>
  surveillanceRouteId
    ? `/surveillance-route/${surveillanceRouteId}/actions-validation/summary/${actionId}`
    : `/work-orders/actions-validation/summary/${actionId}`;
